import React, { useState, useEffect } from 'react';
import { 
  Calendar, Plus, ChevronLeft, ChevronRight, 
  CalendarOff, Clock, Lock, Repeat, Trash2, FileText
} from 'lucide-react';
import api from '../../api/axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { DAY_NAMES, TIME_SLOTS, MONTH_NAMES, DAY_NAMES_SHORT } from './constants';

export const AvailabilityManager = () => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar');
  const [newBlock, setNewBlock] = useState({
    block_type: 'full_day',
    date: '',
    time_slot: '',
    recurring_day: 0,
    recurring_time: '',
    reason: ''
  });

  useEffect(() => {
    fetchBlocks();
  }, []);

  const fetchBlocks = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/calendar/blocks');
      setBlocks(response.data);
    } catch (error) {
      toast.error('Error al cargar bloqueos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBlock = async () => {
    try {
      const data = { ...newBlock };
      
      if (data.block_type === 'full_day') {
        delete data.time_slot;
        delete data.recurring_day;
        delete data.recurring_time;
      } else if (data.block_type === 'time_slot') {
        delete data.recurring_day;
        delete data.recurring_time;
      } else if (data.block_type === 'recurring_day') {
        delete data.date;
        delete data.time_slot;
        delete data.recurring_time;
        data.recurring_day = parseInt(data.recurring_day);
      } else if (data.block_type === 'recurring_time') {
        delete data.date;
        delete data.time_slot;
        data.recurring_day = parseInt(data.recurring_day);
      }
      
      await api.post('/admin/calendar/blocks', data);
      toast.success('Bloqueo creado exitosamente');
      setShowCreateModal(false);
      setNewBlock({
        block_type: 'full_day',
        date: '',
        time_slot: '',
        recurring_day: 0,
        recurring_time: '',
        reason: ''
      });
      fetchBlocks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear bloqueo');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await api.delete(`/admin/calendar/blocks/${blockId}?permanent=true`);
      toast.success('Bloqueo eliminado');
      fetchBlocks();
    } catch (error) {
      toast.error('Error al eliminar bloqueo');
    }
  };

  const getDaysInMonth = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const isDayBlocked = (day) => {
    if (!day) return { blocked: false, partial: false };
    
    const checkDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
    const dayOfWeek = checkDate.getDay();
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    let fullDayBlocked = false;
    let partialBlocked = false;
    
    for (const block of blocks) {
      if (block.block_type === 'full_day' && block.date) {
        const blockDate = new Date(block.date);
        if (blockDate.getDate() === day && blockDate.getMonth() === selectedDate.getMonth() && blockDate.getFullYear() === selectedDate.getFullYear()) {
          fullDayBlocked = true;
        }
      }
      
      if (block.block_type === 'time_slot' && block.date) {
        const blockDate = new Date(block.date);
        if (blockDate.getDate() === day && blockDate.getMonth() === selectedDate.getMonth() && blockDate.getFullYear() === selectedDate.getFullYear()) {
          partialBlocked = true;
        }
      }
      
      if (block.block_type === 'recurring_day' && block.recurring_day === adjustedDay) {
        fullDayBlocked = true;
      }
      
      if (block.block_type === 'recurring_time' && block.recurring_day === adjustedDay) {
        partialBlocked = true;
      }
    }
    
    return { blocked: fullDayBlocked, partial: partialBlocked && !fullDayBlocked };
  };

  const getBlockTypeLabel = (type) => {
    switch (type) {
      case 'full_day': return 'Día Completo';
      case 'time_slot': return 'Horario Específico';
      case 'recurring_day': return 'Día Recurrente';
      case 'recurring_time': return 'Horario Recurrente';
      default: return type;
    }
  };

  const getBlockIcon = (type) => {
    switch (type) {
      case 'full_day': return <CalendarOff className="h-4 w-4" />;
      case 'time_slot': return <Clock className="h-4 w-4" />;
      case 'recurring_day': return <Repeat className="h-4 w-4" />;
      case 'recurring_time': return <Repeat className="h-4 w-4" />;
      default: return <Lock className="h-4 w-4" />;
    }
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const clickedDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
    const dateStr = `${clickedDate.getFullYear()}-${String(clickedDate.getMonth() + 1).padStart(2, '0')}-${String(clickedDate.getDate()).padStart(2, '0')}`;
    
    setNewBlock({
      ...newBlock,
      date: dateStr,
      block_type: 'full_day'
    });
    setShowCreateModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold">Gestión de Disponibilidad</h3>
          <p className="text-sm text-muted-foreground">Click en un día para bloquearlo rápidamente</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={viewMode === 'calendar' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setViewMode('calendar')}
          >
            <Calendar className="h-4 w-4 mr-1" /> Calendario
          </Button>
          <Button 
            variant={viewMode === 'list' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <FileText className="h-4 w-4 mr-1" /> Lista
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="gap-2">
            <Plus className="h-4 w-4" /> Crear Bloqueo
          </Button>
        </div>
      </div>

      {/* Leyenda */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500/40"></div>
          <span>Día bloqueado</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-500/40"></div>
          <span>Horarios bloqueados</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-white/10"></div>
          <span>Disponible</span>
        </div>
      </div>

      {viewMode === 'calendar' ? (
        <div className="space-y-4">
          {/* Navegación del mes */}
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h4 className="text-xl font-bold">
              {MONTH_NAMES[selectedDate.getMonth()]} {selectedDate.getFullYear()}
            </h4>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Calendario */}
          <div className="grid grid-cols-7 gap-1">
            {DAY_NAMES_SHORT.map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                {day}
              </div>
            ))}
            
            {getDaysInMonth().map((day, idx) => {
              const blockStatus = isDayBlocked(day);
              const isToday = day && new Date().toDateString() === new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day).toDateString();
              
              return (
                <div
                  key={idx}
                  onClick={() => handleDayClick(day)}
                  className={`min-h-[70px] p-2 rounded-lg border transition-all ${
                    day ? 'cursor-pointer hover:scale-105' : ''
                  } ${
                    blockStatus.blocked 
                      ? 'bg-red-500/30 border-red-500/50 hover:bg-red-500/40' 
                      : blockStatus.partial
                        ? 'bg-yellow-500/30 border-yellow-500/50 hover:bg-yellow-500/40'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                  } ${isToday ? 'ring-2 ring-primary' : ''}`}
                >
                  {day && (
                    <div className="flex flex-col h-full">
                      <span className={`text-sm font-medium ${isToday ? 'text-primary' : ''}`}>{day}</span>
                      {blockStatus.blocked && (
                        <div className="mt-1">
                          <CalendarOff className="h-3 w-3 text-red-400" />
                        </div>
                      )}
                      {blockStatus.partial && !blockStatus.blocked && (
                        <div className="mt-1">
                          <Clock className="h-3 w-3 text-yellow-400" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Vista de Lista */
        <div className="space-y-2">
          {blocks.length > 0 ? (
            blocks.map((block) => (
              <div key={block.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${
                    block.block_type === 'full_day' ? 'bg-red-500/20 text-red-400' :
                    block.block_type === 'time_slot' ? 'bg-yellow-500/20 text-yellow-400' :
                    block.block_type === 'recurring_day' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-purple-500/20 text-purple-400'
                  }`}>
                    {getBlockIcon(block.block_type)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{getBlockTypeLabel(block.block_type)}</span>
                      {block.block_type.includes('recurring') && (
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs">Recurrente</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {block.block_type === 'full_day' && block.date && (
                        <>Fecha: {new Date(block.date).toLocaleDateString('es-AR')}</>
                      )}
                      {block.block_type === 'time_slot' && (
                        <>Fecha: {new Date(block.date).toLocaleDateString('es-AR')} - {block.time_slot}</>
                      )}
                      {block.block_type === 'recurring_day' && (
                        <>Todos los {block.recurring_day_name}</>
                      )}
                      {block.block_type === 'recurring_time' && (
                        <>Todos los {block.recurring_day_name} a las {block.recurring_time}</>
                      )}
                      {block.reason && <> • {block.reason}</>}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleDeleteBlock(block.id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <CalendarOff className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay bloqueos configurados</p>
              <p className="text-sm">El calendario está completamente disponible</p>
            </div>
          )}
        </div>
      )}

      {/* Modal de crear bloqueo */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-background border border-white/10 rounded-lg w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-6">Crear Bloqueo</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Tipo de Bloqueo</label>
                <select
                  value={newBlock.block_type}
                  onChange={(e) => setNewBlock({ ...newBlock, block_type: e.target.value })}
                  className="w-full p-2 bg-white/5 border border-white/10 rounded"
                >
                  <option value="full_day">Día Completo</option>
                  <option value="time_slot">Horario Específico</option>
                  <option value="recurring_day">Día Recurrente (ej: todos los lunes)</option>
                  <option value="recurring_time">Horario Recurrente (ej: lunes 10:00)</option>
                </select>
              </div>

              {(newBlock.block_type === 'full_day' || newBlock.block_type === 'time_slot') && (
                <div>
                  <label className="block text-sm font-medium mb-2">Fecha</label>
                  <Input
                    type="date"
                    value={newBlock.date}
                    onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })}
                    className="bg-white/5 border-white/10"
                  />
                </div>
              )}

              {newBlock.block_type === 'time_slot' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Horario</label>
                  <select
                    value={newBlock.time_slot}
                    onChange={(e) => setNewBlock({ ...newBlock, time_slot: e.target.value })}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded"
                  >
                    <option value="">Seleccionar horario</option>
                    {TIME_SLOTS.map(slot => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>
              )}

              {(newBlock.block_type === 'recurring_day' || newBlock.block_type === 'recurring_time') && (
                <div>
                  <label className="block text-sm font-medium mb-2">Día de la Semana</label>
                  <select
                    value={newBlock.recurring_day}
                    onChange={(e) => setNewBlock({ ...newBlock, recurring_day: e.target.value })}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded"
                  >
                    {DAY_NAMES.map((day, idx) => (
                      <option key={idx} value={idx}>{day}</option>
                    ))}
                  </select>
                </div>
              )}

              {newBlock.block_type === 'recurring_time' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Horario</label>
                  <select
                    value={newBlock.recurring_time}
                    onChange={(e) => setNewBlock({ ...newBlock, recurring_time: e.target.value })}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded"
                  >
                    <option value="">Seleccionar horario</option>
                    {TIME_SLOTS.map(slot => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Motivo (opcional)</label>
                <Input
                  type="text"
                  value={newBlock.reason}
                  onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
                  placeholder="Ej: Feriado, Mantenimiento, etc."
                  className="bg-white/5 border-white/10"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="ghost" onClick={() => setShowCreateModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleCreateBlock} className="flex-1">
                Crear Bloqueo
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
